import { el, clear, fragment } from '../dom.js';
import { admin, adminRaw } from '../admin-api.js';
import { t, formatMoment } from '../i18n.js';

// The ladder, and nothing more. `institution` was a rung until 2026-08-06,
// when role stopped carrying account type — there is no role to grant here any
// more, so this list only reads.
const ROLE_LABEL = {
  guest: () => t.roleGuest,
  person: () => t.rolePerson,
  moderator: () => t.roleModerator,
  admin: () => t.roleAdmin,
};

// What a reader of this list is actually asking is *what kind of account is
// this*, and `role` stopped answering that in migration 034 — every
// institution row was rewritten to `person`, which is why Salon des Amateurs
// read as a person. The type comes from the server, which asks
// `isInstitution()`; the rank is only worth showing when it is above the
// default, because "Person" beside every row is noise.
function typeLabel(row) {
  return row.institution ? t.roleInstitution : t.rolePerson;
}

function rankLabel(row) {
  return row.role === 'moderator' || row.role === 'admin'
    ? (ROLE_LABEL[row.role] ?? (() => row.role))()
    : null;
}

// Search, ban, unban. Deliberately a search box
// rather than a list: CLAUDE.md allows a moderator to see a person's activity
// only by deliberately opening one account, never from a list and never sorted
// by it, and a browsable roster is the first step towards exactly that.
export async function renderUsers({ state } = {}) {
  // Only an admin sees the rank control. Hiding it is not the boundary — the
  // route asks `requireRole('admin')` and would refuse anyway — it is so a
  // moderator is not shown a button that answers 403, which reads as a broken
  // backend rather than as a permission.
  const isAdmin = state?.session?.user?.role === 'admin';
  const search = el('input', {
    type: 'search',
    id: 'au-q',
    autocomplete: 'off',
    placeholder: t.usersSearch,
    'aria-label': t.usersSearch,
  });

  const list = el('ul', { class: 'card-list' });
  const status = el('p', { class: 'cal-hint', role: 'status' });
  status.hidden = true;

  async function load() {
    const term = search.value.trim();
    clear(list);
    // Nothing typed, nothing listed. The server answers `[]` below two
    // characters anyway; saying so here is what turns an empty screen from a
    // failed search into an instruction.
    if (term.length < 2) {
      list.append(el('li', {}, el('div', { class: 'pub-empty' }, t.usersSearchFirst)));
      return;
    }
    const rows = await admin('GET', `/users?q=${encodeURIComponent(term)}`);
    if (!rows.length) {
      list.append(el('li', {}, el('div', { class: 'pub-empty' }, t.usersEmpty)));
      return;
    }
    for (const row of rows) list.append(userCard(row, load, say, isAdmin));
  }

  function say(message) {
    status.textContent = message;
    status.hidden = false;
  }

  let pending = null;
  search.addEventListener('input', () => {
    clearTimeout(pending);
    pending = setTimeout(load, 200);
  });

  const form = el('form', { class: 'filter-row' }, [search]);
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    load();
  });

  await load();

  return fragment([
    el('h1', { class: 'pub-title' }, t.navUsers),
    el('p', { class: 'pub-lede' }, t.usersLede),
    form,
    status,
    list,
  ]);
}

function userCard(row, reload, say, isAdmin) {
  const banned = Boolean(row.bannedAt);

  // The card that opens the detail view, and the only way into it. There is no
  // link from anywhere else and no listing that ranks by what somebody did —
  // opening one account has to be a decision, which is the second of the four
  // conditions attached to the activity view below.
  const open = el('button', { class: 'button button-quiet', type: 'button' }, t.usersOpen);
  const detail = el('div');
  let shown = false;
  open.addEventListener('click', async () => {
    if (shown) {
      clear(detail);
      shown = false;
      open.textContent = t.usersOpen;
      return;
    }
    const body = await admin('GET', `/users/${row.id}`);
    clear(detail);
    detail.append(detailPanel(body, row, reload, say, isAdmin));
    shown = true;
    open.textContent = t.close;
  });

  const actions = el('div', { class: 'row row-tight' }, [
    open,
    banned ? lift(row, reload, say) : ban(row, reload, say),
  ]);

  return el('li', {}, el('div', { class: 'card' }, [
    el('div', { class: 'row' }, [
      el('h3', { class: 'card-title' }, row.displayName || row.email || `#${row.id}`),
      banned ? el('span', { class: 'badge badge-draft row-end' }, t.usersBanned) : null,
    ]),
    el('p', { class: 'card-meta' }, [
      typeLabel(row),
      // An institution has no e-mail by definition — that is what makes it
      // one — so the address is not a missing field, it is the wrong field.
      // Its address in the product is its nickname.
      row.email ?? (row.nickname ? `/${row.nickname}` : ''),
      rankLabel(row),
      row.institution ? (row.verifiedAt ? t.usersVerified : t.usersUnverified) : null,
    ].filter(Boolean).join(' · ')),
    banned && row.banReason ? el('p', { class: 'card-meta' }, row.banReason) : null,
    actions,
    detail,
  ]));
}

// Identity and moderation history. Activity is not here — it needs the
// deliberate, logged request below, and putting it on the same fetch would
// make every opened account write an audit row for a look nobody took.
function detailPanel(user, row, reload, say, isAdmin) {
  if (!user?.id) return el('p', { class: 'pub-empty' }, t.saveFailed);

  return el('div', { class: 'mod-section' }, [
    el('p', { class: 'card-meta' }, [
      `${t.usersSince}: ${user.createdAt?.slice(0, 10) ?? '—'}`,
      ` · ${t.usersLastSeen}: ${user.lastSeenAt ? formatMoment(user.lastSeenAt) : t.usersNever}`,
    ].join('')),
    el('p', { class: 'card-meta' }, [
      t.usersReportsFiled(user.reportsFiled ?? 0),
      ` · ${t.usersReportsReceived(user.reportsReceived ?? 0)}`,
    ].join('')),

    membershipSection(user, reload, say, isAdmin),
    rankSection(user, reload, say, isAdmin),
    passkeySection(user, reload, say, isAdmin),
    verifySection(user, reload, say),
    activitySection(user),
    exportSection(user),
    deleteSection(user, row, reload, say),
  ]);
}

// Which institutions this person may maintain, and how that changes.
//
// The invitation is the visible control and the default, because a membership
// is the right to publish under somebody else's name and the person's
// agreement belongs inside the operation rather than assumed after it. Direct
// assignment sits below it, is the admin's, and says in its own label that it
// skips the asking — an exception that does not announce itself is one that
// quietly becomes the habit.
function membershipSection(user, reload, say, isAdmin) {
  const picked = { id: null, name: null };
  const found = el('ul', { class: 'card-list' });

  const search = el('input', {
    type: 'search',
    autocomplete: 'off',
    'aria-label': t.membershipSearch,
    placeholder: t.membershipSearch,
  });
  const role = el('select', { 'aria-label': t.membershipRole }, [
    el('option', { value: 'editor' }, t.roleEditor),
    el('option', { value: 'owner' }, t.roleOwner),
  ]);
  const reason = el('input', {
    type: 'text',
    'aria-label': t.membershipReason,
    placeholder: t.membershipReason,
  });
  const chosen = el('p', { class: 'card-meta' }, t.membershipNonePicked);

  let pending = null;
  search.addEventListener('input', () => {
    clearTimeout(pending);
    pending = setTimeout(async () => {
      const term = search.value.trim();
      clear(found);
      if (term.length < 2) return;
      for (const account of await admin('GET', `/accounts?q=${encodeURIComponent(term)}`)) {
        const pick = el('button', { class: 'button button-quiet', type: 'button' },
          `${account.name} · /${account.nickname ?? '—'}`);
        pick.addEventListener('click', () => {
          picked.id = account.id;
          picked.name = account.name;
          chosen.textContent = t.membershipPicked(account.name);
          clear(found);
          search.value = '';
        });
        found.append(el('li', {}, pick));
      }
    }, 200);
  });

  async function submit(direct) {
    if (!picked.id) {
      search.focus();
      return;
    }
    if (!reason.value.trim()) {
      reason.focus();
      return;
    }
    if (direct && !window.confirm(t.membershipAssignConfirm(picked.name))) return;

    const response = await adminRaw('POST', `/users/${user.id}/memberships`, {
      accountId: picked.id, role: role.value, reason: reason.value.trim(), direct,
    });
    if (response.ok) {
      say(direct ? t.membershipAssigned : t.membershipInvited);
      await reload();
      return;
    }
    const body = await response.json().catch(() => ({}));
    say(body.error === 'no_address_to_invite' ? t.membershipNoAddress : t.saveFailed);
  }

  const invite = el('button', { class: 'button', type: 'button' }, t.membershipInvite);
  invite.addEventListener('click', () => submit(false));

  const assign = el('button', { class: 'button button-quiet', type: 'button' }, t.membershipAssign);
  assign.addEventListener('click', () => submit(true));

  return el('section', { class: 'mod-section' }, [
    el('h4', { class: 'bucket-title' }, t.usersMemberships),
    user.memberships?.length
      ? el('ul', { class: 'card-list' }, user.memberships.map((m) =>
          el('li', {}, el('div', { class: 'row' }, [
            el('p', { class: 'card-meta' },
              `${m.name} · ${m.role === 'owner' ? t.roleOwner : t.roleEditor} · /${m.nickname ?? '—'}`),
            isAdmin ? removeMembership(user, m, reload, say) : null,
          ].filter(Boolean)))))
      : el('p', { class: 'pub-empty' }, t.usersNoMemberships),

    el('p', { class: 'card-meta' }, t.membershipHint),
    el('div', { class: 'row' }, [search, role]),
    found,
    chosen,
    el('div', { class: 'row' }, [reason, invite]),
    isAdmin
      ? el('div', {}, [
          el('p', { class: 'card-meta' }, t.membershipAssignHint),
          el('div', { class: 'row row-tight' }, [assign]),
        ])
      : null,
  ].filter(Boolean));
}

function removeMembership(user, membership, reload, say) {
  const button = el('button', { class: 'button button-quiet row-end', type: 'button' }, t.membershipRemove);
  button.addEventListener('click', async () => {
    const why = window.prompt(t.membershipRemoveReason);
    if (why === null || !why.trim()) return;
    button.disabled = true;
    const response = await adminRaw(
      'DELETE',
      `/users/${user.id}/memberships/${membership.accountId}?reason=${encodeURIComponent(why.trim())}`
    );
    button.disabled = false;
    if (response.ok) {
      say(t.membershipRemoved);
      await reload();
      return;
    }
    const body = await response.json().catch(() => ({}));
    say(body.error === 'last_owner' ? t.lastOwner : t.saveFailed);
  });
  return button;
}

// Moderation, granted by an admin. Absent for everybody else — not as the
// boundary (the route asks `requireRole('admin')`), but so a moderator is not
// offered a button that answers 403.
//
// `admin` is not in the list on purpose: an admin who can mint admins removes
// the only rung this product can still revoke from below. That one lives in
// `npm run grant`, which needs a shell on the server — which is also where the
// *first* admin has to come from, since by construction no dashboard can
// produce them.
function rankSection(user, reload, say, isAdmin) {
  if (!isAdmin) return null;
  // A row nobody can sign in as gains nothing from a rank, and putting one on
  // it is how the column came to mean two things in the first place.
  if (user.institution) return null;
  if (user.role === 'admin') {
    return el('section', { class: 'mod-section' }, [
      el('h4', { class: 'bucket-title' }, t.usersRank),
      el('p', { class: 'card-meta' }, `${t.roleAdmin} · ${t.usersRankAdminCli}`),
    ]);
  }

  const promoting = user.role !== 'moderator';
  const reason = el('input', {
    type: 'text',
    'aria-label': t.usersRankReason,
    placeholder: t.usersRankReason,
  });
  const button = el('button', { class: 'button button-quiet', type: 'button' },
    promoting ? t.usersRankGrant : t.usersRankRevoke);

  button.addEventListener('click', async () => {
    if (!reason.value.trim()) {
      reason.focus();
      return;
    }
    if (!window.confirm(promoting
      ? t.usersRankGrantConfirm(user.displayName || user.email || `#${user.id}`)
      : t.usersRankRevokeConfirm(user.displayName || user.email || `#${user.id}`))) return;

    button.disabled = true;
    const response = await adminRaw('POST', `/users/${user.id}/rank`, {
      role: promoting ? 'moderator' : 'person',
      reason: reason.value.trim(),
    });
    button.disabled = false;
    say(response.ok ? t.saved : t.saveFailed);
    if (response.ok) await reload();
  });

  return el('section', { class: 'mod-section' }, [
    el('h4', { class: 'bucket-title' }, t.usersRank),
    el('p', { class: 'card-meta' }, t.usersRankHint),
    el('div', { class: 'row' }, [reason, button]),
  ]);
}

// Whether somebody holds a key is part of answering "why can they not get
// in", so a moderator sees the list. Taking one away is the admin's, like the
// rank beside it.
function passkeySection(user, reload, say, isAdmin) {
  const list = el('ul', { class: 'card-list' });
  const out = el('section', { class: 'mod-section' }, [
    el('h4', { class: 'bucket-title' }, t.adminPasskeys),
    el('p', { class: 'card-meta' }, t.adminPasskeyHint),
    list,
  ]);

  admin('GET', `/users/${user.id}/passkeys`).then((keys) => {
    clear(list);
    if (!keys.length) {
      list.append(el('li', {}, el('p', { class: 'pub-empty' }, t.adminPasskeysNone)));
      return;
    }
    for (const key of keys) {
      const revoke = el('button', { class: 'button button-quiet row-end', type: 'button' }, t.adminPasskeyRevoke);
      revoke.addEventListener('click', async () => {
        const why = window.prompt(t.adminPasskeyReason);
        if (why === null || !why.trim()) return;
        revoke.disabled = true;
        const response = await adminRaw(
          'DELETE', `/users/${user.id}/passkeys/${key.id}?reason=${encodeURIComponent(why.trim())}`
        );
        say(response.ok ? t.adminPasskeyRevoked : t.saveFailed);
        if (response.ok) await reload();
      });

      list.append(el('li', {}, el('div', { class: 'card row' }, [
        el('span', {}, key.label ?? `#${key.id}`),
        el('span', { class: 'card-meta' }, key.lastUsedAt?.slice(0, 10) ?? '—'),
        isAdmin ? revoke : null,
      ].filter(Boolean))));
    }
  });

  return out;
}

// What a *moderator* decides: whether an institution has been claimed —
// `verified_at` — which is what moves the page into the root namespace and
// unlocks the design surface.
function verifySection(user, reload, say) {
  if (!user.institution) return null;

  const reason = el('input', {
    type: 'text',
    'aria-label': t.usersVerifyReason,
    placeholder: t.usersVerifyReason,
  });
  const button = el('button', { class: 'button', type: 'button' }, t.usersVerify);

  if (user.verifiedAt) {
    // No unverify button, and it is not an omission: taking verification away
    // moves a published address back under /i/, and the bio link is the growth
    // loop. The tool for an account that is a problem is the ban.
    return el('section', { class: 'mod-section' }, [
      el('h4', { class: 'bucket-title' }, t.usersVerification),
      el('p', { class: 'card-meta' }, `${t.usersVerified} · ${user.verifiedAt.slice(0, 10)} · /${user.nickname ?? ''}`),
      el('p', { class: 'card-meta' }, t.usersVerifyIrreversible),
    ]);
  }

  button.addEventListener('click', async () => {
    if (!reason.value.trim()) {
      reason.focus();
      return;
    }
    button.disabled = true;
    const response = await adminRaw('POST', `/users/${user.id}/verify`, { reason: reason.value.trim() });
    button.disabled = false;
    say(response.ok ? t.usersVerified : t.saveFailed);
    if (response.ok) await reload();
  });

  return el('section', { class: 'mod-section' }, [
    el('h4', { class: 'bucket-title' }, t.usersVerification),
    el('p', { class: 'card-meta' }, t.usersVerifyHint),
    el('div', { class: 'row' }, [reason, button]),
  ]);
}

// Art. 15 on somebody else's behalf: the same payload the button at /settings
// produces, for a person who wrote in because they cannot sign in to press it.
// It asks for a purpose and writes an audit row for the same reason the
// activity view does — this is one account's whole record leaving the product,
// and the safeguard is that it is one account, on purpose, on the record.
function exportSection(user) {
  const purpose = el('input', {
    type: 'text',
    'aria-label': t.usersExportPurpose,
    placeholder: t.usersExportPurpose,
  });
  const button = el('button', { class: 'button button-quiet', type: 'button' }, t.usersExport);

  button.addEventListener('click', async () => {
    if (!purpose.value.trim()) {
      purpose.focus();
      return;
    }
    button.disabled = true;
    const response = await adminRaw('POST', `/users/${user.id}/export`, { purpose: purpose.value.trim() });
    button.disabled = false;
    if (!response.ok) return;

    // Built in the browser from the JSON the server sent, rather than as a
    // second server route that streams a file: the download and the audited
    // read are then the same request, and there is no URL a copied link could
    // fetch again without one.
    const blob = new Blob([JSON.stringify(await response.json(), null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = el('a', { href: url, download: `tba-tips-export-${user.id}.json` });
    link.click();
    URL.revokeObjectURL(url);
  });

  return el('section', { class: 'mod-section' }, [
    el('h4', { class: 'bucket-title' }, t.usersExport),
    el('p', { class: 'card-meta' }, t.usersExportHint),
    el('div', { class: 'row' }, [purpose, button]),
  ]);
}

// The most sensitive read in the product, and the reason it looks like this.
//
// The purpose is stated *before* anything is fetched, because the third and
// fourth conditions CLAUDE.md attaches to this feature are that the notice
// says it and that the interface names it — a panel that reveals the data and
// explains itself underneath has satisfied neither at the moment it mattered.
// The reason field travels with the request into the audit row, so the log
// records what somebody was looking for rather than only that they looked.
function activitySection(user) {
  const purpose = el('input', {
    type: 'text',
    'aria-label': t.activityReason,
    placeholder: t.activityReason,
  });
  const out = el('div');
  const open = el('button', { class: 'button button-quiet', type: 'button' }, t.activityOpen);

  open.addEventListener('click', async () => {
    open.disabled = true;
    const response = await adminRaw('POST', `/users/${user.id}/activity`, {
      purpose: purpose.value.trim() || null,
    });
    open.disabled = false;
    clear(out);
    if (!response.ok) {
      out.append(el('p', { class: 'pub-error', role: 'alert' }, t.activityFailed));
      return;
    }
    const body = await response.json();
    out.append(
      list(t.activityVotes, body.votes.map((v) =>
        `${v.local_date} · ${v.title}${v.account ? ` · ${v.account}` : ''}`)),
      list(t.activityFollows, body.follows.map((f) => f.name ?? f.nickname)),
    );
  });

  return el('section', { class: 'mod-section' }, [
    el('h4', { class: 'bucket-title' }, t.activityHeading),
    el('p', { class: 'card-meta' }, t.activityPurpose),
    el('p', { class: 'card-meta' }, t.activityLogged),
    el('div', { class: 'row' }, [purpose, open]),
    out,
  ]);
}

function list(title, entries) {
  return el('div', {}, [
    el('h5', { class: 'bucket-title' }, title),
    entries.length
      ? el('ul', { class: 'card-list' }, entries.map((line) => el('li', {},
          el('p', { class: 'card-meta' }, line))))
      : el('p', { class: 'pub-empty' }, t.activityEmpty),
  ]);
}

// Art. 17 by a moderator. Quiet styling and a hint that names the ban as the
// everyday tool, because the failure mode of this button is that it looks like
// the strong version of the one next to it rather than a different decision.
function deleteSection(user, row, reload, say) {
  const reason = el('input', {
    type: 'text',
    'aria-label': t.usersDeleteReason,
    placeholder: t.usersDeleteReason,
  });
  const button = el('button', { class: 'button button-quiet', type: 'button' }, t.usersDelete);

  button.addEventListener('click', async () => {
    if (!reason.value.trim()) {
      reason.focus();
      return;
    }
    if (!window.confirm(t.usersDeleteConfirm(user.displayName || user.email || `#${user.id}`))) return;
    button.disabled = true;
    const response = await adminRaw('POST', `/users/${user.id}/delete`, { reason: reason.value.trim() });
    button.disabled = false;
    say(response.ok ? t.usersDeleted : t.saveFailed);
    if (response.ok) await reload();
  });

  return el('section', { class: 'mod-section' }, [
    el('h4', { class: 'bucket-title' }, t.usersDelete),
    el('p', { class: 'card-meta' }, t.usersDeleteHint),
    el('div', { class: 'row' }, [reason, button]),
  ]);
}

function ban(row, reload, say) {
  const button = el('button', { class: 'button button-quiet', type: 'button' }, t.usersBan);
  button.addEventListener('click', async () => {
    const reason = window.prompt(t.usersBanReason);
    if (reason === null) return;
    button.disabled = true;
    const response = await adminRaw('POST', `/users/${row.id}/ban`, { reason });
    button.disabled = false;
    say(response.ok ? t.saved : t.saveFailed);
    if (response.ok) await reload();
  });
  return button;
}

function lift(row, reload, say) {
  const button = el('button', { class: 'button button-quiet', type: 'button' }, t.usersUnban);
  button.addEventListener('click', async () => {
    button.disabled = true;
    const response = await adminRaw('POST', `/users/${row.id}/unban`, {});
    button.disabled = false;
    say(response.ok ? t.saved : t.saveFailed);
    if (response.ok) await reload();
  });
  return button;
}
